import React from 'react';
import { LuCopy, LuMenu } from 'react-icons/lu';

import { Box, HStack, IconButton, Menu } from '../../index';

interface CopyableCellProps {
  value: unknown;
  children: React.ReactNode;
  enableCopy?: boolean;
  columnId?: string;
  rowId?: string;
  onCopy?: (value: string, columnId?: string, rowId?: string) => void;
}

const formatCellValue = (value: unknown): string => {
  if (value === null || value === undefined) {
    return '';
  }

  if (typeof value === 'boolean') {
    return value ? 'true' : 'false';
  }

  if (typeof value === 'number') {
    return value.toString();
  }

  if (typeof value === 'string') {
    return value;
  }

  if (Array.isArray(value)) {
    return value.join(', ');
  }

  if (typeof value === 'object') {
    try {
      return JSON.stringify(value, null, 2);
    } catch {
      return String(value);
    }
  }

  return String(value);
};

export const CopyableCell: React.FC<CopyableCellProps> = ({
  value,
  children,
  enableCopy = false,
  columnId,
  rowId,
  onCopy,
}) => {
  const [showMenu, setShowMenu] = React.useState(false);

  const handleCopy = async () => {
    const text = formatCellValue(value);

    await navigator.clipboard
      .writeText(text)
      .then(() => {
        onCopy?.(text, columnId, rowId);
      })
      .catch(() => {
        // Silently handle copy errors
      })
      .finally(() => {
        setShowMenu(false);
      });
  };

  if (!enableCopy) {
    return <>{children}</>;
  }

  return (
    <HStack width="full" justifyContent="space-between" alignItems="center" position="relative">
      <Box flex="1" overflow="hidden">
        {children}
      </Box>

      <Menu.Root open={showMenu} onOpenChange={(open) => setShowMenu(open)}>
        <Menu.Trigger asChild>
          <IconButton aria-label="Cell actions" icon={<LuMenu />} size="xs" variant="ghost" />
        </Menu.Trigger>
        <Menu.Positioner>
          <Menu.Content>
            <Menu.Item onClick={handleCopy}>
              <LuCopy />
              Copy cell value
            </Menu.Item>
          </Menu.Content>
        </Menu.Positioner>
      </Menu.Root>
    </HStack>
  );
};
